/* Availability, and the one mistake this platform lets you make with it.

   Availability enters the model in two unrelated places:

     1. batteryPerformance.forcedOutageRatePct and .plannedOutageHoursYear
        multiply into a POWER DERATE that the dispatch optimisation carries at
        every hour of the year. The engine derives it as
            (1 - forced/100) x (1 - planned/8760)
        in BESS_feasibility/Configuration/battery_performance.py, so energy the
        battery could not have delivered is never scheduled in the first place.

     2. feasibility.economics.financialParams.bessAvailabilityPct multiplies
        the SETTLEMENT VALUE of dispatch that was already scheduled.

   Either one on its own is a defensible way to state availability. Both
   together multiply: a battery whose own figures say 97.19% available and a
   97% settlement derate settle at 94.28%, a 5.72% revenue loss where the
   inputs state 2.81%. The report discloses that after the fact
   (revenue_quality.dual_availability_note). This module is what stops it
   being entered - the mistake is cheaper to catch in the form than to explain
   in a footnote.

   The two expressions below are the engine's own, not an approximation of
   them, so the numbers this form quotes are the numbers the run applies. */

/* Blank means neutral: read_config.py reads bessAvailabilityPct with a default
   of 100.0, so an empty field derates nothing. */
export const AVAILABILITY_NEUTRAL_PCT = 100;

/* What the two outage helpers on the Battery Performance page recommend, and
   therefore what a project that models no availability at all is missing. */
export const MARKET_PRACTICE_FORCED_OUTAGE_PCT = 2.0;
export const MARKET_PRACTICE_PLANNED_OUTAGE_HOURS = 72;

const HOURS_PER_YEAR = 8760;

function parseFiniteNumber(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value, low, high) {
  return Math.min(high, Math.max(low, value));
}

/* Exactly battery_performance.py's expression, including its clamps, so the
   factor shown here cannot drift from the factor the dispatch applies.
   availabilityFactor itself is DERIVED there and never read from the saved
   config, which is why this recomputes it from the two inputs the user sees
   rather than trusting a stored value. */
export function milpAvailabilityFactor(batteryPerformance) {
  const bp = batteryPerformance || {};
  const forced = clamp(parseFiniteNumber(bp.forcedOutageRatePct, 0), 0, 100);
  const planned = clamp(parseFiniteNumber(bp.plannedOutageHoursYear, 0), 0, HOURS_PER_YEAR);
  return (1 - forced / 100) * (1 - planned / HOURS_PER_YEAR);
}

/* The revenue-side derate, on the same convention as bess_sizing.py: a blank
   or unparseable figure is 100% (neutral), not zero. */
export function settlementAvailabilityFactor(bessAvailabilityPct) {
  const pct = parseFiniteNumber(bessAvailabilityPct, AVAILABILITY_NEUTRAL_PCT);
  return clamp(pct, 0, 100) / 100;
}

function fmtPct(factor, digits = 2) {
  return `${(factor * 100).toFixed(digits)}%`;
}

function fmtLoss(factor, digits = 2) {
  return `${((1 - factor) * 100).toFixed(digits)}%`;
}

/* null when availability is modelled exactly once - the configuration we want.
   Otherwise the issue, with every number it quotes computed here so the caller
   renders and never derives. */
export function availabilityModellingIssue({ batteryPerformance, bessAvailabilityPct } = {}) {
  const bp = batteryPerformance || {};
  const milpFactor = milpAvailabilityFactor(bp);
  const settlementFactor = settlementAvailabilityFactor(bessAvailabilityPct);
  const combinedFactor = milpFactor * settlementFactor;

  const milpApplied = milpFactor < 1 - 1e-9;
  const settlementApplied = settlementFactor < 1 - 1e-9;

  const base = {
    milpFactor,
    settlementFactor,
    combinedFactor,
    milpApplied,
    settlementApplied,
    forcedOutageRatePct: clamp(parseFiniteNumber(bp.forcedOutageRatePct, 0), 0, 100),
    plannedOutageHoursYear: clamp(
      parseFiniteNumber(bp.plannedOutageHoursYear, 0), 0, HOURS_PER_YEAR),
  };

  /* The same trigger the engine uses (bess_sizing.py dual_availability_flag),
     narrowed to the BESS figure: the MILP derate is a battery power derate, so
     a PV-only settlement derate does not compound with it. */
  if (milpApplied && settlementApplied) {
    return {
      ...base,
      kind: "double_count",
      severity: "warning",
      headline: "Availability is being counted twice.",
      detail:
        `The dispatch already carries a ${fmtPct(milpFactor)} availability power derate, `
        + `built from the forced outage rate (${base.forcedOutageRatePct}%) and planned `
        + `outage hours (${base.plannedOutageHoursYear} h/yr) on the Battery Performance `
        + `page. This ${fmtPct(settlementFactor, 0)} derates the settlement value on top of `
        + `it, so the two multiply to ${fmtPct(combinedFactor)} - a ${fmtLoss(combinedFactor)} `
        + `revenue loss where the battery's own figures state ${fmtLoss(milpFactor)}. `
        + "Keep one of the two: leave this at 100 and let the outage figures carry "
        + "availability (recommended - the derate then acts inside the dispatch, hour by "
        + "hour, so unavailable energy is never scheduled), or zero the outage figures and "
        + "carry availability here. Either way the report discloses the double-count.",
      fix: {
        field: "bessAvailabilityPct",
        value: String(AVAILABILITY_NEUTRAL_PCT),
        label: "Set to 100",
      },
    };
  }

  if (!milpApplied && !settlementApplied) {
    return {
      ...base,
      kind: "not_modelled",
      severity: "info",
      headline: "No availability derate is modelled anywhere.",
      detail:
        "The dispatch runs at 100% availability and the settlement value is not derated, "
        + "so the revenue assumes the battery never goes out of service - a lender will "
        + "ask. Market practice is a forced outage rate near "
        + `${MARKET_PRACTICE_FORCED_OUTAGE_PCT}% with about `
        + `${MARKET_PRACTICE_PLANNED_OUTAGE_HOURS} h/yr of planned maintenance; enter them `
        + "on the Battery Performance page rather than here, so the derate acts inside the "
        + "dispatch instead of on revenue that was already scheduled.",
      fix: null,
    };
  }

  return null;
}
